// How a discount reads (UC-34, UC-35): on the owner's list, in the till's
// Discount sheet and cart, on the Done screen and on both Orders screens.

import { leftToRight } from './bidi';
import { formatMinor } from './money';
import { fill, message, type Language } from '../i18n/messages';
import type { DiscountKind } from '../domain/pricing';

/**
 * What a discount takes, as the owner's list, the till and both Orders
 * screens say it: 20% or 10.00 off, the figure kept left to right.
 */
export function discountRate(
	language: Language,
	kind: DiscountKind,
	value: number,
	minorDigits: number,
): string {
	return kind === 'percent'
		? leftToRight(`${value}%`)
		: fill(message(language, 'discountsAmountRate'), leftToRight(formatMinor(value, minorDigits)));
}

/**
 * A discount by its name and what it takes: "Staff 20%", or "Manager
 * discount 15%" for one a manager typed, which has no name.
 */
export function discountLabel(
	language: Language,
	discount: { name?: string; kind: DiscountKind; value: number },
	minorDigits: number,
): string {
	const name = discount.name || message(language, 'discountManager');
	return `${name} ${discountRate(language, discount.kind, discount.value, minorDigits)}`;
}

/**
 * A discount and what it took off an order, for the Done screen: "Staff
 * 20% · -50.00", the amount kept left to right so its minus stays in front.
 */
export function discountTaken(
	language: Language,
	discount: { name?: string; kind: DiscountKind; value: number },
	takenMinor: number,
	minorDigits: number,
): string {
	const off = leftToRight(formatMinor(-takenMinor, minorDigits));
	return `${discountLabel(language, discount, minorDigits)} · ${off}`;
}
